const {has, pick, isEmpty, cloneDeep} = require('lodash');

// Import only the constants we need
const {FACTOR} = require('../../constants/unitsConversion');
const {TYPE_TEXT, TYPE_LIST} = require('../../constants/types/componentAnalysisConfiguration');

/**
 * Get value adjusted with a factor
 *
 * @param {Object} measure - The measure object
 * @param {boolean} original - Whether to return the original value
 * @return {number|string} The adjusted value
 */
const getValueWithFactor = (measure, original = false) => {
    const value = typeof measure.value === 'string' ? parseFloat(measure.value) : measure.value;
    switch (measure.symbole) {
        case '<':
            return original ? `${value + FACTOR}` : value - FACTOR;
        case '>':
            return original ? `${value - FACTOR}` : value + FACTOR;
        default:
            return measure.value;
    }
};

/**
 * Get color for a range
 *
 * @param {number} value - The value to check
 * @param {Object} norms - The norms object
 * @param {string} symbol - The symbol to use
 * @return {string} The color for the range
 */
const getColorForRange = (value, norms, symbol) => {
    const factoredValue = symbol === '<' ? getValueWithFactor({
        symbole: symbol,
        value,
    }) : value;
    if (factoredValue < norms.intervention) {
        return 'normsGreen';
    }
    if (factoredValue >= norms.intervention && factoredValue < norms.risk) {
        return 'normsYellow';
    }
    if (factoredValue >= norms.risk) {
        return 'normsRed';
    }
    return null;
};

const returnedHtml = (measure, colorClass, averageNorm='') => {
    if (!measure.value || !measure.value.length) {
        return;
    }
    if (measure.type === TYPE_LIST || measure.type === TYPE_TEXT) {
        // As this types doesn't have norm color nor measureUnit they have same height as measure-norm-block
        return `
                <div style="display: flex; flex-direction: row; align-items: center; height: 56px;">
                    <div>${ measure.value }</div>
                </div>`;
    }

    if (measure.symbole) {
        const cond = measure.symbole === '=';
        const symbole = cond ? `` : `
                ${measure.symbole}`;
        return `<div style="display: flex; flex-direction: column;">
                   <div style="display: flex; height: 25px; margin-top: 5px;">
                        <div style="display: flex; width:100%;">
                            <fieldset class="fieldsetStyle" >
                                <div class="measure-box">
                                    <span class="value">${symbole} ${ measure.value }</span>
                                </div>
                            </fieldset>
                        </div>
                    </div>
                    <div style=" display:flex; flex-direction: row; align-items: center;
                        justify-content: center; height: 25px; width: 100px; border-radius: 5px;" class="${colorClass}">
                        <div> <span style="font-size: 16px;">${averageNorm}</span></div>
                    </div>
                </div>`;
    }
    return `<div style="display: flex; flex-direction: column;">
                <fieldset class="fieldsetStyle">
                    <div class="measure-box">
                        <span class="value">${ measure.value }</span>
                    </div>
                    <div> <span>${averageNorm}</span></div>
                </fieldset>
                <div style=" display:flex; flex-direction: row; align-items: center;
                    justify-content: center; height: 25px; width: 100px; border-radius: 5px;" class="${colorClass}">
                    <div> <span style="font-size: 16px;">${averageNorm}</span></div>
                </div>
            </div>`;
};

/**
 * Get the color for Legio norms
 *
 * @param {Object} value - The value object
 * @param {Object} norms - The norms object
 * @param {string} averageNorm - The average norm
 * @param {boolean} isBackend - Whether this is for backend use
 * @return {string} The color or HTML for the norm
 */
const getLegioNormsColor = (value, norms, averageNorm, isBackend = true) => {
    const parsedValue = parseFloat(value.value);
    if (value.symbole === '>') {
        return isBackend ? returnedHtml(value, 'normsRed', averageNorm) : 'normsRed';
    }
    if (parsedValue === norms.intervention || parsedValue === norms.risk) {
        const color = getColorForRange(parsedValue, norms, value.symbole);
        return isBackend ? returnedHtml(value, color, averageNorm) : color;
    }
    const color = getColorForRange(parsedValue, norms, value.symbole);
    return isBackend ? returnedHtml(value, color, averageNorm) : color;
};

/**
 * Check if a value is defined
 *
 * @param {*} value - The value to check
 * @return {boolean} Whether the value is defined
 */
const isDefined = (value) => value !== null && value !== undefined && value !== '';

/**
 * Get the average norm
     *
     * @param {Object} norm - The norm object
     * @return {string} The average norm
     */
const getNormAverage = (norm) => {
    if (!norm) {
        return '';
    }

    const {veryLow, low, minimum, maximum, high, veryHigh} = norm;

    if (isDefined(minimum) && isDefined(maximum)) {
        return `${minimum} - ${maximum}`;
    }

    if (isDefined(veryLow) && isDefined(low) && isDefined(minimum) && !isDefined(maximum)) {
        return `≥ ${minimum}`;
    }

    if (isDefined(maximum) && isDefined(high) && isDefined(veryHigh) && !isDefined(minimum)) {
        return `≤ ${maximum}`;
    }
};

/**
 * Change color based on value and norm
 * @param {number} value
 * @param {Object} norm
 * @param {number | null} lowestValue
 * @param {number | null} highestValue
 * @param {Object | undefined} options
 * @return {string | null}
 */
const changeColor = (
    value,
    norm,
    lowestValue,
    highestValue,
    options,
) => {
    // Ensure options is defined and has the necessary properties, set defaults if not
    const {symbol = ''} = options || {};

    // Check if norm is null or undefined, return null to avoid further execution
    if (norm == null) return null;

    // If value is empty or NaN, return null
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return null;
    // Check if values of norm object are empty or null
    const norms = {...norm};
    delete norms.ref;
    if (Object.values(norms).every((x) => x === null || x === '')) return null;
    let colorClass = 'normsGreen';

    // If no highest value and lowest value and norm does not belong to legio standard
    if (!norms.risk && !highestValue && !lowestValue) return null;
    let ret = false;

    // check for legionella norms
    if (norms.risk && norms.intervention) {
        return getLegioNormsColor({value: value.toString(), symbole: symbol}, norms, null, false);
    }

    // Check for Simple standard norms
    // we have first 3 norm level(VeryLow, Low, Minimum)
    if (!isDefined(norm.veryHigh) && !isDefined(norm.high) && !isDefined(norm.maximum)) {
        if (norm.minimum && value < norm.minimum) {
            colorClass = 'normsYellow';
            ret = true;
        }

        if (value < (norm.low || 0) && norm.low) {
            colorClass = 'normsOrange';
            ret = true;
        }

        if (value < (norm.veryLow || 0) && norm.veryLow) {
            colorClass = 'normsRed';
            ret = true;
        }

        if (ret) return colorClass;
    }

    // we have last 3 norm level(veryHigh, Maximum, high)
    if (!isDefined(norm.minimum) && !isDefined(norm.low) && !isDefined(norm.veryLow)) {
        if (norm.maximum && value > norm.maximum) {
            colorClass = 'normsYellow';
            ret = true;
        }
        if (value > (norm.high || 0) && norm.high) {
            colorClass = 'normsOrange';
            ret = true;
        }
        if (value > (norm.veryHigh || 0) && norm.veryHigh) {
            colorClass = 'normsRed';
            ret = true;
        }

        if (ret) {
            return colorClass;
        }
    }

    // we have all 6 possible norm levels(simple standard)
    if (isDefined(norm.veryHigh) && isDefined(norm.veryLow) && isDefined(norm.minimum)) {
        if (norm.minimum && value < norm.minimum) {
            if (value < norm.minimum) {
                colorClass = 'normsYellow';
                ret = true;
            }

            if (value < (norm.low || 0) && norm.low) {
                colorClass = 'normsOrange';
                ret = true;
            }

            if (value < (norm.veryLow || 0) && norm.veryLow) {
                colorClass = 'normsRed';
                ret = true;
            }

            if (ret) {
                return colorClass;
            }
        }

        if (norm.maximum && value > norm.maximum) {
            if (value > norm.maximum) {
                colorClass = 'normsYellow';
                ret = true;
            }

            if (value > (norm.high || 0) && norm.high) {
                colorClass = 'normsOrange';
                ret = true;
            }

            if (value > (norm.veryHigh || 0) && norm.veryHigh) {
                colorClass = 'normsRed';
                ret = true;
            }

            if (ret) {
                return colorClass;
            }
        }
    }

    // we have 2 possible norm levels (Minimum & Maximum)
    if (
        !isDefined(norm.veryHigh) &&
        !isDefined(norm.veryLow) &&
        isDefined(norm.minimum) &&
        isDefined(norm.maximum)
    ) {
        if (lowestValue !== null && value < lowestValue) {
            colorClass = 'normsRed';
            ret = true;
        }
        if (lowestValue !== null && value >= lowestValue) {
            colorClass = 'normsGreen';
            ret = true;
        }
        if (highestValue !== null && value > highestValue) {
            colorClass = 'normsRed';
            ret = true;
        }
    }
    return colorClass;
};


/**
 * Set limits and determine color
 * @param {number} value
 * @param {Object} norm
 * @param {string | null} options
 * @return {string | null}
 */
const setLimits = (value, norm, options) => {
    let lowestValue = null;
    let highestValue = null;

    // Getting the lowest value
    if (norm && isDefined(norm.veryLow)) {
        lowestValue = norm.veryLow;
    } else if (norm && isDefined(norm.low)) {
        lowestValue = norm.low;
    } else if (norm && isDefined(norm.minimum)) {
        lowestValue = norm.minimum;
    } else {
        lowestValue = null;
    }

    if (norm && isDefined(norm.veryHigh)) {
        highestValue = norm.veryHigh;
    } else if (norm && isDefined(norm.high)) {
        highestValue = norm.high;
    } else if (norm && isDefined(norm.maximum)) {
        highestValue = norm.maximum;
    } else {
        highestValue = null;
    }
    return changeColor(value, norm, lowestValue, highestValue, options);
};

/**
 *
 * @param {Object} value
 * @param {Object} value.norm - The norms object
 * @param {string} value.value - The value to check
 * @param {Object} stats - The stats object
 *
 * @returns {string | undefined} The color or HTML for the norm
 */

const normColor = (value, stats) => {
    const measure = parseFloat(value.value);
    const clonedNorm = cloneDeep(value.norm);
    const norm = pick(clonedNorm, ['low', 'veryLow', 'minimum', 'maximum', 'veryHigh', 'high', 'risk', 'intervention']); // to avoid extra keys and avoid get wrong 'normKeysLen'

    let colorClass = 'normsUndefined';
    const averageNorm = getNormAverage(value.norm);
    if (!norm || isEmpty(norm)) {
        if (stats) {
            Object.assign(stats, {colorClass});
        }
        return returnedHtml(value, colorClass, averageNorm);
    }
    const normKeysLen = Object.keys(norm).length;
    const {veryLow, low, minimum, maximum, high, veryHigh}= norm;
    const {risk, intervention} = norm;

    // if norm is nrom simple standard (it would have 4 combinations)
    if (has(norm, 'veryLow') || has(norm, 'minimum') || has(norm, 'maximum') || has(norm, 'veryHigh')) {
        // Min and Max combination
        if (normKeysLen === 2 && has(norm, 'minimum') && has(norm, 'maximum')) {
            if (measure < minimum) colorClass = 'normsRed';
            if (measure >= minimum && measure <= maximum) colorClass = 'normsGreen';
            if (measure > maximum) colorClass = 'normsRed';
        } else {
            // for all levels and combination of 3 levels
            if (normKeysLen === 3 && has(norm, 'veryLow')) norm.lastLevelMin = minimum;
            if (has(norm, 'veryHigh')) norm.lastLevelHigh = veryHigh;

            if (has(norm, 'veryLow') && (measure < veryLow)) {
                colorClass = 'normsRed';
            } if (has(norm, 'low') && (measure < low && measure >= veryLow)) {
                colorClass= 'normsOrange';
            } if (has(norm, 'minimum') && (measure< minimum && measure >= low)) {// che length
                colorClass = 'normsYellow';
            } if (has(norm, 'lastLevelMin') && measure >= minimum) {
                colorClass = 'normsGreen';
            } if (has(norm, 'maximum')) {
                if (normKeysLen === 3 && measure <= maximum) colorClass = 'normsGreen';
                if (normKeysLen === 6 && measure <= maximum && measure >= minimum) colorClass = 'normsGreen';
            }
            if (has(norm, 'high') && measure > maximum && measure <= high) {
                colorClass = 'normsYellow';
            }
            if (has(norm, 'veryHigh') && measure > high && measure <= veryHigh) {
                colorClass = 'normsOrange';
            }
            if (has(norm, 'lastLevelHigh') && measure > veryHigh) colorClass = 'normsRed';
        }
        if (stats) {
            Object.assign(stats, {colorClass});
        }
        return returnedHtml(value, colorClass, averageNorm);
    }
    if (risk && intervention) {
        return getLegioNormsColor(value, {risk, intervention}, averageNorm);
    }
};

module.exports = {
    getLegioNormsColor,
    getColorForRange,
    getValueWithFactor,
    returnedHtml,
    normColor,
    isDefined,
    getNormAverage,
    changeColor,
    setLimits,
};
